import { NgClass } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import { DokumenteExplorerFolder } from './dossier.models';
import { NewFolderDraft, NewFolderForm, PLANNED_FOLDER_GUID } from './new-folder-form';

/** Whether guid names this folder or any folder below it. */
export function containsFolder(folders: DokumenteExplorerFolder[], guid: string | null): boolean {
  return (
    guid !== null &&
    folders.some((folder) => folder.guid === guid || containsFolder(folder.children, guid))
  );
}

/**
 * Renders one level of the document explorer and itself for every child level,
 * since the payload does not bound the nesting depth. Each branch opens and
 * closes on its own; a branch holding the selected folder starts open, so
 * reopening the step shows where the pick sits rather than the bare top level.
 * A branch also opens while a new folder is being named in it, since the
 * form sits where the folder will appear. The planned folder, not created yet,
 * shows marked as new, with a button to drop it in place of the plus.
 */
@Component({
  selector: 'cmi-folder-tree',
  imports: [NgClass, TranslatePipe, FolderTree, NewFolderForm],
  templateUrl: './folder-tree.html'
})
export class FolderTree {
  public readonly folders = input.required<DokumenteExplorerFolder[]>();
  public readonly selectedGuid = input<string | null>(null);
  /** Branches of this level that start open; not passed on to the levels below. */
  public readonly startOpen = input<boolean>(false);
  public readonly newFolder = input<NewFolderDraft | null>(null);
  /** False hides the plus buttons: one new folder at a time. */
  public readonly canCreate = input<boolean>(true);
  public readonly folderSelect = output<DokumenteExplorerFolder>();
  public readonly newFolderStart = output<DokumenteExplorerFolder>();
  public readonly newFolderTitleChange = output<string>();
  public readonly newFolderAdd = output<void>();
  public readonly newFolderDiscard = output<void>();
  public readonly plannedFolderRemove = output<void>();

  protected readonly plannedGuid = PLANNED_FOLDER_GUID;

  /** Only a level with a branch to open keeps room for the chevron. */
  protected readonly hasBranches = computed(() =>
    this.folders().some((folder) => folder.children.length > 0)
  );

  /** Branches the user opened or closed by hand, overriding the default. */
  private readonly _toggled = signal<ReadonlyMap<string, boolean>>(new Map());

  protected isOpen(folder: DokumenteExplorerFolder): boolean {
    return (
      this.isNewFolderParent(folder) ||
      (this._toggled().get(folder.guid) ??
        (this.startOpen() || containsFolder(folder.children, this.selectedGuid())))
    );
  }

  /** Children with no branch to open sit a little further in, clear of the parent's icon. */
  protected hasChildBranches(folder: DokumenteExplorerFolder): boolean {
    return folder.children.some((child) => child.children.length > 0);
  }

  protected isNewFolderParent(folder: DokumenteExplorerFolder): boolean {
    return this.newFolder()?.parentGuid === folder.guid;
  }

  /** Forgets a hand-closed branch, so the folder planned in it shows once selected. */
  protected onNewFolderStart(folder: DokumenteExplorerFolder): void {
    const toggled = new Map(this._toggled());
    toggled.delete(folder.guid);
    this._toggled.set(toggled);
    this.newFolderStart.emit(folder);
  }

  /** A branch holding the new folder form stays open until the form closes. */
  protected onToggle(folder: DokumenteExplorerFolder): void {
    if (this.isNewFolderParent(folder)) {
      return;
    }

    const toggled = new Map(this._toggled());
    toggled.set(folder.guid, !this.isOpen(folder));
    this._toggled.set(toggled);
  }
}
